import { FunctionComponent } from 'react';
import { Table } from 'components/tables/Table';
import { Subscription } from 'domain/subscription/interfaces/Subscription';
import { RequestStatus } from 'types/RequestStatus';
import { columnFilters } from './columnFilters';
import { ColumnDef, Row } from '@tanstack/react-table';
import { FilterSettings } from 'components/tables/Table.interfaces';
import { ExpandedSubscriptionContent } from './ExpandedSubscriptionContent/ExpandedSubscriptionContent';
import { useNavigate } from 'react-router-dom';
import { AdminRole } from 'domain/account/enums/AdminRole';
import useAuthRoles from 'hooks/useAuthRoles';
import { subscriptionCanBeEdited } from 'utils/subscriptionCanBeEdited';
import { useAccountPageContext } from 'pages/Account/Accounts/AccountProfilePage/AccountPageContext';

const SubscriptionTable: FunctionComponent<{
	columns: ColumnDef<Subscription, any>[];
	requestStatus: RequestStatus;
	subscriptions: Subscription[];
	heading?: string;
	filters?: FilterSettings<Subscription>;
	showRowCount?: boolean;
	expandable?: boolean;
	selectable?: boolean | ((row: Row<Subscription>) => boolean);
	onRowClicked?: (subscription: Subscription) => void;
	excludeBundledSubscriptions?: boolean;
	initialSorting?: { id: string; desc: boolean }[];
}> = ({
	columns,
	requestStatus,
	subscriptions,
	heading = 'Subscriptions',
	filters = columnFilters,
	showRowCount = false,
	expandable = true,
	selectable = false,
	onRowClicked,
	excludeBundledSubscriptions = false,
	initialSorting = [
		{ id: 'startDate' satisfies keyof Subscription, desc: true },
	],
}) => {
	const navigate = useNavigate();
	const { adminIsRestrictedToRole: isRestrictedToRole } = useAuthRoles();
	const contextAccount = useAccountPageContext()?.account;

	return (
		<Table<Subscription>
			header={heading}
			filterSettings={filters}
			initialSorting={initialSorting}
			columns={columns}
			containerGap="xs"
			alternateRowBackground
			requestStatus={requestStatus}
			data={
				excludeBundledSubscriptions
					? subscriptions?.filter((sub) => !sub.bundleId)
					: subscriptions
			}
			noDataText="No subscriptions"
			paginationOptions={{
				showFromCount: 10,
			}}
			withPagination
			showRowCount={showRowCount}
			withExpandableRows={expandable}
			expandableComponent={(subscription: Subscription) => (
				<ExpandedSubscriptionContent subscription={subscription} />
			)}
			onRowClicked={onRowClicked}
			withSelection={selectable}
			actionMenuItems={[
				{
					name: 'View',
					onClick: (subscription) =>
						navigate(`/subscriptions/${subscription.id}`),
				},
				{
					name: 'Edit',
					onClick: (subscription) =>
						navigate(
							contextAccount?.company.id
								? `/subscriptions/${subscription.id}/edit?companyId=${contextAccount.company.id}`
								: `/subscriptions/${subscription.id}/edit`,
						),
					isDisabled: (subscription) =>
						!subscriptionCanBeEdited(
							subscription.status,
							subscription.type,
							isRestrictedToRole(AdminRole.SALES),
						),
				},
			]}
		/>
	);
};

export default SubscriptionTable;
